import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { TabPanel, TabView } from "primereact/tabview";
import React, { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { UpdateUserProfile } from "../models/user.model";
import { citiesService } from "../service/cities.service";
import { clientService } from "../service/clientService";
import { countryService } from "../service/country.service";
import { loginService } from "../service/login.service";
import { proposalService } from "../service/proposal.service";
import { rmcService } from "../service/rmcService";
import { roomTypeService } from "../service/roomType.service";
import { statesService } from "../service/state.service";
import { userService } from "../service/user.service";
import "./MyProfile.css";
import PageHeader from "./PageHeader";

const MyProfile: React.FC = () => {
  const user = useSelector((state: any) => state.auth.user) || {};
  const userRole = useSelector((state: any) => state.auth.userRole) ?? "";
  const dispatch = useDispatch();

  // Removed console.log for debugging

  const [firstName, setFirstName] = useState(user.firstName || "");
  const [lastName, setLastName] = useState(user.lastName || "");
  const [email, setEmail] = useState(user.email || "");
  const [mobile, setMobile] = useState(user.mobile || user.phone || "");

  // Helper function to get the selected address from the addresses array
  const getSelectedAddress = (addresses: any[]) => {
    if (!addresses || addresses.length === 0) return null;

    // Look for office or primary type address first, then any address
    let selectedAddress = addresses.find((addr) => {
      const addressType = addr.type || addr.Type;
      return (
        addressType &&
        (addressType.toLowerCase() === "office" ||
          addressType.toLowerCase() === "primary")
      );
    });

    // If no specific type, use the first available address
    if (!selectedAddress) {
      selectedAddress = addresses[0];
    }

    return selectedAddress;
  };
  const [dob, setDob] = useState(user.dob || "");
  const [addressDetails, setAddressDetails] = useState({
    doorNumber: "",
    buildingName: "",
    address1: "",
    address2: "",
    pincode: "",
    phone: "",
    checkIn: "",
    checkOut: "",
    noOfAdults: "",
    noOfChildrens: "",
    countryId: "",
    stateId: "",
    cityId: "",
  });

  const [countries, setCountries] = useState<{ id: number; name: string }[]>(
    []
  );
  const [states, setStates] = useState<{ id: number; name: string }[]>([]);
  const [cities, setCities] = useState<{ id: number; name: string }[]>([]);
  const [roomTypes, setRoomTypes] = useState<{ id: number; name: string }[]>(
    []
  );
  const [selectedRoomTypeIds, setSelectedRoomTypeIds] = useState<number[]>([]);
  const [pendingPrefIds, setPendingPrefIds] = useState<number[] | null>(null);
  const [rmcs, setRmcs] = useState<{ id: number; name: string }[]>([]);
  const [selectedRmcId, setSelectedRmcId] = useState<number | null>(null);
  const [clients, setClients] = useState<{ id: number; name: string }[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [proposals, setProposals] = useState<{ id: number; name: string }[]>(
    []
  );
  const [selectedProposalId, setSelectedProposalId] = useState<number | null>(
    null
  );

  // Helper to fetch proposals given rmcId and clientId
  const fetchProposals = async (
    rmcId: number | null,
    clientId: number | null
  ) => {
    if (userRole !== "B2BUser") return;
    if (!rmcId || !clientId) {
      setProposals([]);
      setSelectedProposalId(null);
      return;
    }
    try {
      const list = await proposalService.getProposalListItems(
        Number(rmcId),
        Number(clientId)
      );
      const normalized = (list || []).map((p: any) => ({
        id: Number(
          p.id ?? p.value ?? p.Value ?? p.proposalId ?? p.ProposalId ?? 0
        ),
        name: p.name ?? p.Name ?? p.label ?? p.Label ?? String(p),
      }));
      setProposals(normalized.filter((p: any) => p && p.id));
      if (selectedProposalId) {
        const stillValid = normalized.some(
          (p: any) => Number(p.id) === Number(selectedProposalId)
        );
        if (!stillValid) setSelectedProposalId(null);
      }
    } catch (err) {
      setProposals([]);
      setSelectedProposalId(null);
    }
  };

  const formatDateDisplay = (val: any) => {
    if (val === undefined || val === null || val === "") return "";
    try {
      const d = val instanceof Date ? val : new Date(val);
      if (isNaN(d.getTime())) return String(val);
      return d.toLocaleString();
    } catch {
      return String(val);
    }
  };

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const userData = await userService.getCurrentUserDetails();
        setFirstName(userData.firstName || "");
        setLastName(userData.lastName || "");
        setEmail(userData.email || "");
        setMobile(userData.phoneNumber || userData.phone || "");

        // Preselect room type preferences if present (handle $values wrapper from .NET responses)
        if (userData.preferences) {
          try {
            let prefs: any = userData.preferences;
            if (prefs && typeof prefs === "object" && "$values" in prefs)
              prefs = prefs.$values;
            if (Array.isArray(prefs) && prefs.length > 0) {
              const ids = prefs
                .map(
                  (p: any) =>
                    p.roomTypeId ??
                    p.RoomTypeId ??
                    p.roomType?.id ??
                    p.roomType?.Id
                )
                .filter((v: any) => v !== undefined && v !== null)
                .map((v: any) => Number(v));
              // If roomTypes already loaded we can set directly, otherwise store pending ids and seed names if available
              if (roomTypes && roomTypes.length > 0) {
                const valid = ids.filter((id: number) =>
                  roomTypes.some((r) => Number(r.id) === Number(id))
                );
                setSelectedRoomTypeIds(valid);
              } else {
                setPendingPrefIds(ids);
                // If prefs include names, seed roomTypes so MultiSelect can display chips immediately
                try {
                  const prefsArray = Array.isArray(prefs) ? prefs : [];
                  const seeded = prefsArray
                    .map((p: any) => {
                      const id = Number(
                        p.roomTypeId ??
                          p.RoomTypeId ??
                          p.roomType?.id ??
                          p.roomType?.Id ??
                          0
                      );
                      const name =
                        p.name ??
                        p.Name ??
                        p.roomType?.name ??
                        p.roomType?.Name ??
                        undefined;
                      return id && name ? { id, name } : null;
                    })
                    .filter((x: any) => x !== null) as {
                    id: number;
                    name: string;
                  }[];
                  if (seeded.length > 0) {
                    setRoomTypes((prev) => {
                      const map = new Map<number, string>();
                      (prev || []).forEach((r) =>
                        map.set(Number(r.id), r.name)
                      );
                      seeded.forEach((s) => {
                        if (!map.has(Number(s.id)))
                          map.set(Number(s.id), s.name);
                      });
                      return Array.from(map.entries()).map(([id, name]) => ({
                        id: Number(id),
                        name,
                      }));
                    });
                  }
                } catch {}
              }
            }
          } catch (err) {
            console.warn(
              "MyProfile - unable to parse user preferences for room types",
              err
            );
          }
        }
        const uidRmc = userData.rmcId;
        if (uidRmc) {
          try {
            setSelectedRmcId(Number(uidRmc));
          } catch {}
        }
        // Set office address if user is B2BUser and has address data
        // Removed latestUserData state
        if (userRole === "B2BUser") {
          // Handle both direct array and $values wrapped array from API
          let addresses = userData.address;

          // Check if the response is wrapped in $values (common with .NET Core JSON serialization)
          if (
            addresses &&
            typeof addresses === "object" &&
            "$values" in addresses
          ) {
            addresses = (addresses as any).$values;
          }

          if (addresses && Array.isArray(addresses) && addresses.length > 0) {
            const selectedAddress = getSelectedAddress(addresses);
            if (selectedAddress) {
              // extract booking-related fields (handle different casings)
              const raw = selectedAddress as any;
              const checkIn =
                raw.checkIn ?? raw.CheckIn ?? raw.checkin ?? raw.Checkin ?? "";
              const checkOut =
                raw.checkOut ??
                raw.CheckOut ??
                raw.checkout ??
                raw.Checkout ??
                "";
              const noOfAdults =
                raw.noOfAdults ?? raw.NoOfAdults ?? raw.no_of_adults ?? "";
              const noOfChildrens =
                raw.noOfChildrens ??
                raw.NoOfChildrens ??
                raw.no_of_childrens ??
                "";

              setAddressDetails({
                doorNumber:
                  selectedAddress.doorNumber ||
                  selectedAddress.DoorNumber ||
                  "",
                buildingName:
                  selectedAddress.buildingName ||
                  selectedAddress.BuildingName ||
                  "",
                address1:
                  selectedAddress.address1 || selectedAddress.Address1 || "",
                address2:
                  selectedAddress.address2 || selectedAddress.Address2 || "",
                pincode:
                  selectedAddress.pincode || selectedAddress.Pincode || "",
                phone: (selectedAddress.phone ?? selectedAddress.Phone) || "",
                countryId:
                  selectedAddress.countryId ?? selectedAddress.CountryId ?? "",
                stateId:
                  selectedAddress.stateId ?? selectedAddress.StateId ?? "",
                cityId: selectedAddress.cityId ?? selectedAddress.CityId ?? "",
                checkIn: checkIn,
                checkOut: checkOut,
                noOfAdults:
                  noOfAdults !== undefined && noOfAdults !== null
                    ? String(noOfAdults)
                    : "",
                noOfChildrens:
                  noOfChildrens !== undefined && noOfChildrens !== null
                    ? String(noOfChildrens)
                    : "",
              });
              // Preselect RMC if provided in user data (handle different casings)
              const uidRmc = userData.rmcId;
              if (uidRmc) {
                try {
                  setSelectedRmcId(Number(uidRmc));
                } catch {}
              }
              // Preselect client if rmcUserClientMappings present on user data
              try {
                const u: any = userData as any;
                let mappings: any =
                  u.rmcUserClientMappings ??
                  u.RmcUserClientMappings ??
                  u.rmcUserClientMapping;
                if (
                  mappings &&
                  typeof mappings === "object" &&
                  "$values" in mappings
                )
                  mappings = mappings.$values;
                if (Array.isArray(mappings) && mappings.length > 0) {
                  const first = mappings[0];
                  const cid =
                    first.clientId ??
                    first.ClientId ??
                    first.client?.id ??
                    first.client?.ClientId ??
                    first.client?.clientId;
                  if (cid !== undefined && cid !== null) {
                    try {
                      setSelectedClientId(Number(cid));
                    } catch {}
                  }
                }
              } catch (err) {}
              // Preselect proposal if provided in the selected address
              try {
                const addr: any = selectedAddress as any;
                const pid =
                  addr.proposalId ??
                  addr.ProposalId ??
                  addr.proposal?.id ??
                  addr.proposal?.ProposalId ??
                  addr.proposal?.proposalId;
                if (pid !== undefined && pid !== null) {
                  try {
                    setSelectedProposalId(Number(pid));
                  } catch {}
                }
              } catch (err) {}
            }
          } else {
            setAddressDetails({
              doorNumber: "",
              buildingName: "",
              address1: "",
              address2: "",
              pincode: "",
              phone: "",
              countryId: "",
              stateId: "",
              cityId: "",
              checkIn: "",
              checkOut: "",
              noOfAdults: "",
              noOfChildrens: "",
            });
          }
        }
      } catch (error) {
        toast.error("Failed to fetch user details", {
          position: "bottom-center",
          autoClose: 3000,
        });
      }
    };

    fetchUserDetails();
  }, [userRole]);

  // Load countries on mount
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const list = await countryService.getListItems();
        setCountries(
          list.map((c: any) => ({ id: Number(c.id), name: c.name }))
        );
      } catch (err) {
        setCountries([]);
      }
    };
    loadCountries();
  }, []);

  useEffect(() => {
    const loadRoomTypes = async () => {
      try {
        let list: any = await roomTypeService.getAllRoomTypes();
        // raw result may be wrapped in $values
        if (list && typeof list === "object" && "$values" in list)
          list = list.$values;

        if (!Array.isArray(list) || list.length === 0) {
          try {
            const listItems = await roomTypeService.getListItems();
            // fallback used
            list = listItems;
          } catch (fallbackErr) {
            console.warn(
              "MyProfile - roomTypeService.getListItems fallback failed",
              fallbackErr
            );
            list = [];
          }
        }

        const normalized = (list || []).map((r: any) => ({
          id: Number(
            r.id ?? r.roomTypeId ?? r.RoomTypeId ?? r.value ?? r.Value ?? 0
          ),
          name: r.name ?? r.Name ?? r.label ?? r.Label ?? String(r),
        }));
        setRoomTypes(normalized);
        // set normalized room types

        // If we had pending preference ids from user fetch, apply them now (filter against normalized ids)
        if (pendingPrefIds && pendingPrefIds.length > 0) {
          const valid = pendingPrefIds.filter((id) =>
            normalized.some((r: any) => Number(r.id) === Number(id))
          );
          setSelectedRoomTypeIds(valid);
          setPendingPrefIds(null);
        }
      } catch (err) {
        setRoomTypes([]);
      }
    };
    loadRoomTypes();
  }, []);

  // Ensure selectedRoomTypeIds contain only ids that exist in roomTypes
  useEffect(() => {
    if (!roomTypes || roomTypes.length === 0) return;
    if (!selectedRoomTypeIds || selectedRoomTypeIds.length === 0) return;
    const valid = selectedRoomTypeIds.filter((id) =>
      roomTypes.some((r) => Number(r.id) === Number(id))
    );
    if (valid.length !== selectedRoomTypeIds.length) {
      setSelectedRoomTypeIds(valid);
    }
  }, [roomTypes, selectedRoomTypeIds]);

  // Apply pending preference ids once roomTypes are available (handles either load order)
  useEffect(() => {
    if (
      !roomTypes ||
      roomTypes.length === 0 ||
      !pendingPrefIds ||
      pendingPrefIds.length === 0
    )
      return;
    const valid = pendingPrefIds.filter((id) =>
      roomTypes.some((r) => Number(r.id) === Number(id))
    );
    setSelectedRoomTypeIds(valid);
    setPendingPrefIds(null);
  }, [roomTypes, pendingPrefIds]);

  // ...existing code...

  // Load states when countryId changes
  useEffect(() => {
    const cid = addressDetails.countryId;
    if (!cid) {
      setStates([]);
      return;
    }
    const loadStates = async () => {
      try {
        const list = await statesService.getByCountryId(Number(cid));
        setStates(list.map((s: any) => ({ id: Number(s.id), name: s.name })));
      } catch (err) {
        setStates([]);
      }
    };
    loadStates();
  }, [addressDetails.countryId]);

  // Load cities when stateId changes
  useEffect(() => {
    const sid = addressDetails.stateId;
    if (!sid) {
      setCities([]);
      return;
    }
    const loadCities = async () => {
      try {
        const list = await citiesService.getByStateId(Number(sid));
        setCities(list.map((c: any) => ({ id: Number(c.id), name: c.name })));
      } catch (err) {
        setCities([]);
      }
    };
    loadCities();
  }, [addressDetails.stateId]);

  // Load RMCs on mount
  useEffect(() => {
    const loadRmcs = async () => {
      try {
        let list: any = [];
        try {
          list = await rmcService.getListItems();
        } catch (err) {
          try {
            list = await rmcService.getAll();
          } catch {
            list = [];
          }
        }
        if (list && typeof list === "object" && "$values" in list)
          list = list.$values;
        const normalized = (list || []).map((r: any) => ({
          id: Number(r.id ?? r.value ?? r.Value ?? 0),
          name: r.name ?? r.Name ?? String(r),
        }));
        setRmcs((normalized as any[]).filter((r: any) => r && r.id));
      } catch (err) {
        setRmcs([]);
      }
    };
    loadRmcs();
  }, []);

  // Load clients when selectedRmcId changes
  useEffect(() => {
    // Only load clients for B2B users
    if (userRole !== "B2BUser") return;
    const loadClients = async (rmcId: number | null) => {
      if (!rmcId) {
        setClients([]);
        setSelectedClientId(null);
        return;
      }
      try {
        const list = await clientService.getClientsByRmcId(Number(rmcId));
        const normalized = (list || []).map((c: any) => ({
          id: Number(c.id ?? c.clientId ?? c.ClientId ?? c.value ?? 0),
          name: c.name ?? c.Name ?? c.clientName ?? c.ClientName ?? String(c),
        }));
        setClients(normalized.filter((c: any) => c && c.id));
        // don't auto-select a client; keep any existing selection if still valid
        if (selectedClientId) {
          const stillValid = normalized.some(
            (c: any) => Number(c.id) === Number(selectedClientId)
          );
          if (!stillValid) setSelectedClientId(null);
        }
      } catch (err) {
        setClients([]);
        setSelectedClientId(null);
      }
    };
    loadClients(selectedRmcId);
  }, [selectedRmcId, userRole]);

  // Load proposals when both RMC and Client are selected (only for B2B users)
  useEffect(() => {
    // use helper to fetch proposals
    fetchProposals(selectedRmcId, selectedClientId);
  }, [selectedRmcId, selectedClientId, userRole]);
  const [profilePic, setProfilePic] = useState(user.profilePic || null);

  // Generate user initials from full name
  const getUserInitials = (firstName: string, lastName: string): string => {
    const first = firstName?.trim() || "";
    const last = lastName?.trim() || "";

    if (!first && !last) return "U";
    if (!last) return first.charAt(0).toUpperCase();

    return (first.charAt(0) + last.charAt(0)).toUpperCase();
  };

  const userInitials = getUserInitials(firstName, lastName);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Password validation function
  const validatePassword = (
    password: string
  ): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (password.length < 8) {
      errors.push("Password must be at least 8 characters long");
    }
    if (!/[a-z]/.test(password)) {
      errors.push("Password must include lowercase letters");
    }
    if (!/[A-Z]/.test(password)) {
      errors.push("Password must include uppercase letters");
    }
    if (!/[0-9]/.test(password)) {
      errors.push("Password must include at least one number");
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push("Password must include at least one special character");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  // Check if password is valid
  const isPasswordValid = validatePassword(newPassword).isValid;
  const passwordsMatch = newPassword === confirmPassword;
  const isPasswordFormValid =
    currentPassword &&
    newPassword &&
    confirmPassword &&
    isPasswordValid &&
    passwordsMatch;

  const handleProfilePicChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      // File selected, could be used for future upload functionality
      setProfilePic(URL.createObjectURL(e.target.files[0]));
    }
  };
  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const userData = await userService.getCurrentUserDetails();
      const updateData: UpdateUserProfile = {
        id: userData.id,
        firstName,
        lastName,
      };

      await userService.updateUserProfile(updateData);
      toast.success("Profile updated successfully!", {
        position: "bottom-center",
        autoClose: 3000,
      });
    } catch (error) {
      toast.error("Failed to update profile.", {
        position: "bottom-center",
        autoClose: 3000,
      });
    }
  };
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password requirements
    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      toast.error(validation.errors.join(". "), {
        position: "bottom-center",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirm password do not match.", {
        position: "bottom-center",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    try {
      await loginService.changePassword({
        currentPassword,
        confirmPassword,
      });
      dispatch({ type: "auth/logout" });
    } catch (error) {
      toast.error("Failed to change password.", {
        position: "bottom-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  return (
    <>
      <div className="my-profile-container">
        <div className="content">
          <PageHeader title="My Profile" showBackButton={false} />
          <div className="content-body">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-lg-10">
                  <div className="card">
                    <TabView>
                      <TabPanel header="Primary Information">
                        <Form onSubmit={handleProfileSave}>
                          <div className="row">
                            <div className="col-md-4">
                              <div className="my-profile-avatar-section">
                                <div className="avatar">
                                  {profilePic ? (
                                    <img src={profilePic} alt="Profile" />
                                  ) : (
                                    <div className="avatar-initials">
                                      {userInitials}
                                    </div>
                                  )}
                                </div>
                                <Form.Label className="btn btn-outline-secondary btn-sm mb-0">
                                  <Icon.Camera className="me-1" size={14} />
                                  Change Picture
                                  <Form.Control
                                    type="file"
                                    accept="image/*"
                                    onChange={handleProfilePicChange}
                                    style={{ display: "none" }}
                                  />
                                </Form.Label>
                                <small className="text-muted mt-2 d-block text-center my-profile-avatar-hint">
                                  Upload a professional photo
                                </small>
                              </div>
                            </div>
                            <div className="col-md-8">
                              <div className="my-profile-section-header d-flex align-items-center gap-2">
                                <Icon.PersonCircle className="me-2" size={22} />
                                <span>Personal Information</span>
                              </div>
                              <div className="my-profile-instructions mb-3">
                                <Icon.InfoCircle className="me-1" size={14} />
                                Please ensure your name and contact details are
                                up to date. Email and mobile number cannot be
                                changed.
                              </div>
                              <div className="my-profile-two-column">
                                <div className="d-flex flex-column gap-2 form-group">
                                  <label htmlFor="firstname">First Name</label>
                                  <InputText
                                    id="firstname"
                                    name="firstname"
                                    placeholder="Enter your First Name"
                                    value={firstName}
                                    onChange={(e) =>
                                      setFirstName(e.target.value)
                                    }
                                    required
                                  />
                                </div>
                                <div className="d-flex flex-column gap-2 form-group">
                                  <label htmlFor="lastname">Last Name</label>
                                  <InputText
                                    id="lastname"
                                    name="lastname"
                                    placeholder="Enter your Last Name"
                                    value={lastName}
                                    onChange={(e) =>
                                      setLastName(e.target.value)
                                    }
                                    required
                                  />
                                </div>
                                <div className="d-flex flex-column gap-2 form-group">
                                  <label htmlFor="email">Email</label>
                                  <InputText
                                    id="email"
                                    name="email"
                                    value={email}
                                    readOnly
                                    style={{
                                      backgroundColor: "#f8f9fa",
                                      cursor: "not-allowed",
                                      color: "#6c757d",
                                    }}
                                  />
                                </div>
                                <div className="d-flex flex-column gap-2 form-group">
                                  <label htmlFor="mobile">Mobile Number</label>
                                  <InputText
                                    id="mobile"
                                    name="mobile"
                                    value={mobile}
                                    readOnly
                                    style={{
                                      backgroundColor: "#f8f9fa",
                                      cursor: "not-allowed",
                                      color: "#6c757d",
                                    }}
                                  />
                                </div>
                              </div>

                              <div className="my-profile-subsection-header d-flex align-items-center gap-2 mt-3">
                                <Icon.Building
                                  className="me-2 text-secondary"
                                  size={18}
                                />
                                <span>Organization Details</span>
                              </div>
                              <div className="my-profile-instructions mb-3">
                                <Icon.ShieldLock className="me-1" size={14} />
                                Your company and client information (read-only).
                                Contact your administrator to update these
                                details.
                              </div>
                              <div className="my-profile-two-column">
                                <div className="d-flex flex-column gap-2 form-group">
                                  <label htmlFor="rmc">RMC</label>
                                  <Dropdown
                                    id="rmc"
                                    value={selectedRmcId}
                                    options={[
                                      { id: null, name: "Not available" },
                                      ...rmcs.map((r) => ({
                                        id: r.id,
                                        name: r.name,
                                      })),
                                    ]}
                                    onChange={(e) =>
                                      setSelectedRmcId(e.value ?? null)
                                    }
                                    optionLabel="name"
                                    optionValue="id"
                                    placeholder="Not available"
                                    className="w-100"
                                    disabled={true}
                                  />
                                </div>
                                <div></div>

                                {userRole === "B2BUser" && (
                                  <>
                                    <div className="d-flex flex-column gap-2 form-group">
                                      <label htmlFor="client">Client</label>
                                      <Dropdown
                                        id="client"
                                        value={selectedClientId}
                                        options={[
                                          { id: null, name: "Not available" },
                                          ...clients.map((c) => ({
                                            id: c.id,
                                            name: c.name,
                                          })),
                                        ]}
                                        onChange={(e) => {
                                          const v = e.value ?? null;
                                          setSelectedClientId(v);
                                          fetchProposals(selectedRmcId, v);
                                        }}
                                        optionLabel="name"
                                        optionValue="id"
                                        placeholder="Not available"
                                        className="w-100"
                                        disabled={true}
                                      />
                                    </div>
                                    <div className="d-flex flex-column gap-2 form-group">
                                      <label htmlFor="proposal">Proposal</label>
                                      <Dropdown
                                        id="proposal"
                                        value={selectedProposalId}
                                        options={[
                                          { id: null, name: "Not available" },
                                          ...proposals.map((p) => ({
                                            id: p.id,
                                            name: p.name,
                                          })),
                                        ]}
                                        onChange={(e) =>
                                          setSelectedProposalId(e.value ?? null)
                                        }
                                        optionLabel="name"
                                        optionValue="id"
                                        placeholder="Not available"
                                        className="w-100"
                                        disabled={true}
                                      />
                                    </div>
                                  </>
                                )}
                              </div>

                              {/* Office Address - Only for B2BUser */}
                              {userRole === "B2BUser" && (
                                <>
                                  <div className="my-profile-subsection-header d-flex align-items-center gap-2 mt-3">
                                    <Icon.GeoAlt
                                      className="me-2 text-danger"
                                      size={18}
                                    />
                                    <span>Office Address</span>
                                  </div>
                                  <div className="my-profile-instructions mb-3">
                                    <Icon.Envelope className="me-1" size={14} />
                                    Your registered office address for business
                                    correspondence and official communications.
                                  </div>
                                  <div className="my-profile-two-column">
                                    <div className="d-flex flex-column gap-2 form-group">
                                      <label htmlFor="doorNumber">
                                        Door/Unit Number
                                      </label>
                                      <InputText
                                        id="doorNumber"
                                        name="doorNumber"
                                        placeholder="Not available"
                                        value={addressDetails.doorNumber}
                                        readOnly
                                        style={{
                                          backgroundColor: "#f8f9fa",
                                          cursor: "not-allowed",
                                          color: "#6c757d",
                                        }}
                                      />
                                    </div>

                                    <div className="d-flex flex-column gap-2 form-group">
                                      <label htmlFor="buildingName">
                                        Building/Company Name
                                      </label>
                                      <InputText
                                        id="buildingName"
                                        name="buildingName"
                                        placeholder="Not available"
                                        value={addressDetails.buildingName}
                                        readOnly
                                        style={{
                                          backgroundColor: "#f8f9fa",
                                          cursor: "not-allowed",
                                          color: "#6c757d",
                                        }}
                                      />
                                    </div>

                                    <div className="d-flex flex-column gap-2 form-group">
                                      <label htmlFor="address1">
                                        Address 1
                                      </label>
                                      <InputText
                                        id="address1"
                                        name="address1"
                                        placeholder="Not available"
                                        value={addressDetails.address1}
                                        readOnly
                                        style={{
                                          backgroundColor: "#f8f9fa",
                                          cursor: "not-allowed",
                                          color: "#6c757d",
                                        }}
                                      />
                                    </div>

                                    <div className="d-flex flex-column gap-2 form-group">
                                      <label htmlFor="address2">
                                        Address 2
                                      </label>
                                      <InputText
                                        id="address2"
                                        name="address2"
                                        placeholder="Not available"
                                        value={addressDetails.address2}
                                        readOnly
                                        style={{
                                          backgroundColor: "#f8f9fa",
                                          cursor: "not-allowed",
                                          color: "#6c757d",
                                        }}
                                      />
                                    </div>

                                    <div className="d-flex flex-column gap-2 form-group">
                                      <label htmlFor="pincode">Pincode</label>
                                      <InputText
                                        id="pincode"
                                        name="pincode"
                                        placeholder="Not available"
                                        value={addressDetails.pincode}
                                        readOnly
                                        style={{
                                          backgroundColor: "#f8f9fa",
                                          cursor: "not-allowed",
                                          color: "#6c757d",
                                        }}
                                      />
                                    </div>

                                    <div className="d-flex flex-column gap-2 form-group">
                                      <label htmlFor="phone">Phone</label>
                                      <InputText
                                        id="phone"
                                        name="phone"
                                        placeholder="Not available"
                                        value={addressDetails.phone}
                                        readOnly
                                        style={{
                                          backgroundColor: "#f8f9fa",
                                          cursor: "not-allowed",
                                          color: "#6c757d",
                                        }}
                                      />
                                    </div>

                                    <div className="d-flex flex-column gap-2 form-group">
                                      <label htmlFor="country">Country</label>
                                      <Dropdown
                                        id="country"
                                        value={
                                          addressDetails.countryId
                                            ? Number(addressDetails.countryId)
                                            : null
                                        }
                                        options={countries.map((c) => ({
                                          label: c.name,
                                          value: c.id,
                                        }))}
                                        onChange={(e) =>
                                          setAddressDetails((prev) => ({
                                            ...prev,
                                            countryId: e.value
                                              ? String(e.value)
                                              : "",
                                            stateId: "",
                                            cityId: "",
                                          }))
                                        }
                                        placeholder="Not available"
                                        className="w-100"
                                        disabled={true}
                                      />
                                    </div>

                                    <div className="d-flex flex-column gap-2 form-group">
                                      <label htmlFor="state">State</label>
                                      <Dropdown
                                        id="state"
                                        value={
                                          addressDetails.stateId
                                            ? Number(addressDetails.stateId)
                                            : null
                                        }
                                        options={states.map((s) => ({
                                          label: s.name,
                                          value: s.id,
                                        }))}
                                        onChange={(e) =>
                                          setAddressDetails((prev) => ({
                                            ...prev,
                                            stateId: e.value
                                              ? String(e.value)
                                              : "",
                                            cityId: "",
                                          }))
                                        }
                                        placeholder="Not available"
                                        className="w-100"
                                        disabled={true}
                                      />
                                    </div>

                                    <div className="d-flex flex-column gap-2 form-group">
                                      <label htmlFor="city">City</label>
                                      <Dropdown
                                        id="city"
                                        value={
                                          addressDetails.cityId
                                            ? Number(addressDetails.cityId)
                                            : null
                                        }
                                        options={cities.map((c) => ({
                                          label: c.name,
                                          value: c.id,
                                        }))}
                                        onChange={(e) =>
                                          setAddressDetails((prev) => ({
                                            ...prev,
                                            cityId: e.value
                                              ? String(e.value)
                                              : "",
                                          }))
                                        }
                                        placeholder="Not available"
                                        className="w-100"
                                        disabled={true}
                                      />
                                    </div>
                                  </div>

                                  <div className="my-profile-subsection-header d-flex align-items-center gap-2 mt-3">
                                    <Icon.HouseDoor
                                      className="me-2 text-success"
                                      size={18}
                                    />
                                    <span>Room Type Preferences</span>
                                  </div>
                                  <div className="my-profile-instructions mb-3">
                                    <Icon.StarFill className="me-1" size={14} />
                                    Select your preferred room types for future
                                    bookings. This helps us show you the most
                                    relevant accommodation options.
                                  </div>
                                  <div className="my-profile-field-group">
                                    <div className="d-flex flex-column gap-2 form-group">
                                      <label htmlFor="roomTypePreferences">
                                        Preferred Room Types
                                      </label>
                                      <MultiSelect
                                        id="roomTypePreferences"
                                        value={selectedRoomTypeIds}
                                        options={roomTypes.map((r) => ({
                                          label: r.name,
                                          value: r.id,
                                        }))}
                                        onChange={(e) =>
                                          setSelectedRoomTypeIds(
                                            Array.isArray(e.value)
                                              ? e.value.map((v: any) =>
                                                  Number(v)
                                                )
                                              : []
                                          )
                                        }
                                        placeholder="Not available"
                                        className="w-100"
                                        disabled={true}
                                        display="chip"
                                        showClear
                                      />
                                    </div>
                                  </div>

                                  <div className="my-profile-subsection-header d-flex align-items-center gap-2 mt-3">
                                    <Icon.CalendarCheck
                                      className="me-2 text-info"
                                      size={18}
                                    />
                                    <span>Booking Details</span>
                                  </div>
                                  <div className="my-profile-instructions mb-3">
                                    <Icon.Clock className="me-1" size={14} />
                                    These details reflect your current or most
                                    recent booking reservation. Updated
                                    automatically when you make a new booking.
                                  </div>
                                  <div className="my-profile-two-column">
                                    <div className="d-flex flex-column gap-2 form-group">
                                      <label htmlFor="checkin">Check In</label>
                                      <InputText
                                        id="checkin"
                                        name="checkin"
                                        placeholder="Not available"
                                        value={formatDateDisplay(
                                          addressDetails.checkIn
                                        )}
                                        readOnly
                                        style={{
                                          backgroundColor: "#f8f9fa",
                                          cursor: "not-allowed",
                                          color: "#6c757d",
                                        }}
                                      />
                                    </div>

                                    <div className="d-flex flex-column gap-2 form-group">
                                      <label htmlFor="checkout">
                                        Check Out
                                      </label>
                                      <InputText
                                        id="checkout"
                                        name="checkout"
                                        placeholder="Not available"
                                        value={formatDateDisplay(
                                          addressDetails.checkOut
                                        )}
                                        readOnly
                                        style={{
                                          backgroundColor: "#f8f9fa",
                                          cursor: "not-allowed",
                                          color: "#6c757d",
                                        }}
                                      />
                                    </div>

                                    <div className="d-flex flex-column gap-2 form-group">
                                      <label htmlFor="noOfAdults">
                                        No. of Adults
                                      </label>
                                      <InputText
                                        id="noOfAdults"
                                        name="noOfAdults"
                                        placeholder="Not available"
                                        value={addressDetails.noOfAdults}
                                        readOnly
                                        style={{
                                          backgroundColor: "#f8f9fa",
                                          cursor: "not-allowed",
                                          color: "#6c757d",
                                        }}
                                      />
                                    </div>

                                    <div className="d-flex flex-column gap-2 form-group">
                                      <label htmlFor="noOfChildrens">
                                        No. of Children
                                      </label>
                                      <InputText
                                        id="noOfChildrens"
                                        name="noOfChildrens"
                                        placeholder="Not available"
                                        value={addressDetails.noOfChildrens}
                                        readOnly
                                        style={{
                                          backgroundColor: "#f8f9fa",
                                          cursor: "not-allowed",
                                          color: "#6c757d",
                                        }}
                                      />
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="my-profile-save-reminder mt-3 mb-2">
                            <Icon.CheckCircle
                              className="me-1 text-success"
                              size={14}
                            />
                            <small>
                              Remember to save your changes before leaving this
                              page.
                            </small>
                          </div>
                          <div className="my-profile-actions">
                            <Button type="reset" className="btn btn-secondary">
                              Reset
                            </Button>
                            <Button type="submit" className="btn btn-primary">
                              <Icon.Save className="me-1" size={16} />
                              Save Changes
                            </Button>
                          </div>
                        </Form>
                      </TabPanel>
                      <TabPanel header="Change Password">
                        <Form onSubmit={handlePasswordChange}>
                          <div className="row">
                            <div className="col-lg-7">
                              <div className="my-profile-section-header d-flex align-items-center gap-2">
                                <Icon.ShieldCheck
                                  className="me-2 text-primary"
                                  size={22}
                                />
                                <span>Update Password</span>
                              </div>
                              <div className="my-profile-instructions mb-3">
                                <Icon.ExclamationTriangle
                                  className="me-1 text-warning"
                                  size={14}
                                />
                                Keep your account secure by using a strong
                                password. You will be logged out after changing
                                your password.
                              </div>
                              <div className="my-profile-field-group">
                                <div className="d-flex flex-column gap-2 form-group">
                                  <label htmlFor="currentpassword">
                                    Current Password
                                  </label>
                                  <InputText
                                    type="password"
                                    name="currentpassword"
                                    id="currentpassword"
                                    value={currentPassword}
                                    onChange={(e) =>
                                      setCurrentPassword(e.target.value)
                                    }
                                  />
                                </div>
                                <div className="d-flex flex-column gap-2 form-group">
                                  <label htmlFor="newpassword">
                                    New Password
                                  </label>
                                  <InputText
                                    name="newpassword"
                                    id="newpassword"
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) =>
                                      setNewPassword(e.target.value)
                                    }
                                  />
                                </div>

                                <div className="d-flex flex-column gap-2 form-group">
                                  <label htmlFor="cnewpassword">
                                    Confirm New Password
                                  </label>
                                  <InputText
                                    name="cnewpassword"
                                    id="cnewpassword"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) =>
                                      setConfirmPassword(e.target.value)
                                    }
                                    className={
                                      confirmPassword &&
                                      (passwordsMatch ? "p-valid" : "p-invalid")
                                    }
                                  />
                                  {confirmPassword && !passwordsMatch && (
                                    <small className="text-danger">
                                      Passwords do not match
                                    </small>
                                  )}
                                  {confirmPassword && passwordsMatch && (
                                    <small className="text-success">
                                      <Icon.CheckCircleFill
                                        className="me-1"
                                        size={12}
                                      />
                                      Passwords match
                                    </small>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="col-lg-5">
                              <div className="my-profile-password-tips p-3">
                                <div className="d-flex align-items-start mb-2">
                                  <Icon.Lightbulb
                                    className="me-2 text-warning lightbulb-icon"
                                    size={16}
                                  />
                                  <div>
                                    <strong className="d-block mb-2">
                                      Password Requirements:
                                    </strong>
                                    <ul className="mb-0 ps-3 my-profile-password-requirements-list">
                                      <li
                                        className={
                                          newPassword.length >= 8
                                            ? "text-success"
                                            : ""
                                        }
                                      >
                                        {newPassword.length >= 8 ? (
                                          <Icon.CheckCircleFill
                                            className="me-1"
                                            size={12}
                                          />
                                        ) : (
                                          <Icon.Circle
                                            className="me-1"
                                            size={12}
                                          />
                                        )}
                                        At least 8 characters long
                                      </li>
                                      <li
                                        className={
                                          /[A-Z]/.test(newPassword) &&
                                          /[a-z]/.test(newPassword)
                                            ? "text-success"
                                            : ""
                                        }
                                      >
                                        {/[A-Z]/.test(newPassword) &&
                                        /[a-z]/.test(newPassword) ? (
                                          <Icon.CheckCircleFill
                                            className="me-1"
                                            size={12}
                                          />
                                        ) : (
                                          <Icon.Circle
                                            className="me-1"
                                            size={12}
                                          />
                                        )}
                                        Include uppercase and lowercase letters
                                      </li>
                                      <li
                                        className={
                                          /[0-9]/.test(newPassword)
                                            ? "text-success"
                                            : ""
                                        }
                                      >
                                        {/[0-9]/.test(newPassword) ? (
                                          <Icon.CheckCircleFill
                                            className="me-1"
                                            size={12}
                                          />
                                        ) : (
                                          <Icon.Circle
                                            className="me-1"
                                            size={12}
                                          />
                                        )}
                                        Include at least one number
                                      </li>
                                      <li
                                        className={
                                          /[!@#$%^&*(),.?":{}|<>]/.test(
                                            newPassword
                                          )
                                            ? "text-success"
                                            : ""
                                        }
                                      >
                                        {/[!@#$%^&*(),.?":{}|<>]/.test(
                                          newPassword
                                        ) ? (
                                          <Icon.CheckCircleFill
                                            className="me-1"
                                            size={12}
                                          />
                                        ) : (
                                          <Icon.Circle
                                            className="me-1"
                                            size={12}
                                          />
                                        )}
                                        Include at least one special character
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="my-profile-actions">
                            <Button
                              type="submit"
                              className="btn btn-primary"
                              disabled={!isPasswordFormValid}
                            >
                              <Icon.Key className="me-1" size={16} />
                              Change Password
                            </Button>
                          </div>
                        </Form>
                      </TabPanel>
                    </TabView>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default MyProfile;
